import React, { Component } from 'react';
import { FormattedMessage } from 'react-intl';
import QRCode from 'qrcode.react';
import Barcode from 'react-barcode';
import CanvasElement from './CanvasElement';
import {
    mmToPx,
    pxToMm,
    getPageDimsPx,
    getPageDimsMm,
    createDefaultElement,
    SAMPLE_BINDINGS,
    getPreviewText,
    getVerticalAlignCss,
    getDropShadowCss,
    SHAPE_CLIP_PATHS,
    getInitialPreviewLang,
    isSafeImageUrl,
} from './templateUtils';

const GRID_MM = 10;

class DesignerCanvas extends Component {
    constructor(props) {
        super(props);
        this.canvasRef = React.createRef();
        this.state = { showGrid: false };
    }

    componentDidMount() {
        // Keyboard listener for arrow-key nudging
        window.addEventListener('keydown', this._onKeyDown);
    }

    componentWillUnmount() {
        window.removeEventListener('keydown', this._onKeyDown);
    }

    _onKeyDown = (e) => {
        const { selectedElementId, elements, onMoveElement, onDuplicateElement, template } = this.props;
        if (!selectedElementId) return;
        if (document.activeElement && ['INPUT', 'TEXTAREA'].includes(document.activeElement.tagName)) return;

        // Ctrl+D duplicates the selected element
        if (e.ctrlKey && (e.key === 'd' || e.key === 'D')) {
            e.preventDefault();
            if (onDuplicateElement) onDuplicateElement(selectedElementId);
            return;
        }

        if (!['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) return;

        // Prevent page scroll
        e.preventDefault();

        const el = elements.find(el => el.id === selectedElementId);
        if (!el || el.locked) return;

        const step = e.shiftKey ? 10 : 1;
        const dims = getPageDimsMm(template.pageSize, template.orientation);

        let newX = el.x;
        let newY = el.y;

        if (e.key === 'ArrowLeft')  newX = Math.max(0, el.x - step);
        if (e.key === 'ArrowRight') newX = Math.min(dims.w - el.w, el.x + step);
        if (e.key === 'ArrowUp')    newY = Math.max(0, el.y - step);
        if (e.key === 'ArrowDown')  newY = Math.min(dims.h - el.h, el.y + step);

        onMoveElement(selectedElementId, newX, newY);
    };

    _onDragOver = (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'copy';
    };

    _onDrop = (e) => {
        e.preventDefault();
        const type = e.dataTransfer.getData('elementType');
        if (!type) return;

        const { template, zoom, onAddElement } = this.props;
        const dims = getPageDimsMm(template.pageSize, template.orientation);

        // Calculate drop position relative to canvas
        const canvasRect = this.canvasRef.current
            ? this.canvasRef.current.getBoundingClientRect()
            : null;

        let el;
        if (canvasRect) {
            const relX = e.clientX - canvasRect.left;
            const relY = e.clientY - canvasRect.top;
            const dropXMm = pxToMm(relX, zoom);
            const dropYMm = pxToMm(relY, zoom);

            el = createDefaultElement(type, dims.w, dims.h);
            el.x = Math.max(0, Math.min(dropXMm - el.w / 2, dims.w - el.w));
            el.y = Math.max(0, Math.min(dropYMm - el.h / 2, dims.h - el.h));
        } else {
            el = createDefaultElement(type, dims.w, dims.h);
        }

        onAddElement(el);
    };

    _onCanvasClick = () => {
        // Deselect when clicking canvas background
        this.props.onSelectElement(null);
    };

    _renderElementContent = (el, zoom) => {
        // Sizes in pre-transform space (canvas applies transform:scale(zoom))
        const w = mmToPx(el.w, 1);
        const h = mmToPx(el.h, 1);
        const { previewLang = getInitialPreviewLang() } = this.props;
        const value = el.binding ? (SAMPLE_BINDINGS[el.binding] || el.binding) : getPreviewText(el.freeText, previewLang);

        switch (el.type) {
            case 'text':
                return (
                    <div
                        style={{
                            width: '100%',
                            height: '100%',
                            ...(getVerticalAlignCss(el.verticalAlign) ? { display: 'flex', flexDirection: 'column', justifyContent: getVerticalAlignCss(el.verticalAlign) } : {}),
                            fontFamily: el.fontFamily || 'Arial',
                            fontSize: el.fontSize || 14,
                            fontWeight: el.bold ? 'bold' : 'normal',
                            fontStyle: el.italic ? 'italic' : 'normal',
                            textDecoration: el.underline ? 'underline' : 'none',
                            color: el.colour || '#333333',
                            textAlign: el.textAlign || 'left',
                            backgroundColor: el.bgColour || 'transparent',
                            border: (el.borderWidth || 0) > 0
                                ? `${el.borderWidth}px solid ${el.borderColour || '#000000'}`
                                : 'none',
                            borderRadius: `${el.borderRadius || 0}px`,
                            padding: `${el.padding ?? 4}px`,
                            boxSizing: 'border-box',
                            overflow: 'hidden',
                            lineHeight: el.lineHeight || 1.2,
                            letterSpacing: `${el.letterSpacing || 0}px`,
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            pointerEvents: 'none',
                        }}
                    >
                        {value || ' '}
                    </div>
                );

            case 'image':
                // Safe URL Validation (defense-in-depth): only render http(s) image
                // sources; anything else (javascript:, data:, malformed) falls through
                // to the neutral placeholder. Protects both manual and AI-sourced images.
                return isSafeImageUrl(el.src) ? (
                    <img
                        src={el.src}
                        alt=""
                        style={{ width: '100%', height: '100%', objectFit: el.fit || 'contain', borderRadius: `${el.borderRadius || 0}px`, pointerEvents: 'none' }}
                    />
                ) : (
                    <div
                        style={{
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: '1px dashed #adb5bd',
                            color: '#adb5bd',
                            fontSize: '0.75rem',
                            pointerEvents: 'none',
                        }}
                    >
                        <i className="fa-solid fa-image mr-1" /> Image
                    </div>
                );

            case 'qr': {
                const qrValue = value || 'QR';
                const qrSize = Math.min(w, h) - 4;
                return (
                    <div data-qr-id={el.id} style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                        <QRCode value={qrValue} size={Math.max(20, qrSize)} fgColor={el.qrColour || '#000000'} bgColor={el.qrBgColour || '#ffffff'} />
                    </div>
                );
            }

            case 'barcode': {
                const bcValue = value || '123456789';
                return (
                    <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', pointerEvents: 'none' }}>
                        <Barcode
                            value={bcValue}
                            width={1}
                            height={Math.max(20, h - 10)}
                            fontSize={10}
                            margin={0}
                        />
                    </div>
                );
            }

            case 'shape': {
                if (el.shapeType === 'line') {
                    return (
                        <div
                            style={{
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                pointerEvents: 'none',
                            }}
                        >
                            <div
                                style={{
                                    width: '100%',
                                    height: el.borderWidth ?? 1,
                                    backgroundColor: el.borderColour || '#000000',
                                }}
                            />
                        </div>
                    );
                }
                if (el.shapeType === 'ellipse') {
                    return (
                        <div
                            style={{
                                width: '100%',
                                height: '100%',
                                backgroundColor: el.fillColour || '#ffffff',
                                border: (el.borderWidth ?? 1) > 0 ? `${el.borderWidth ?? 1}px solid ${el.borderColour || '#000000'}` : 'none',
                                borderRadius: '50%',
                                boxSizing: 'border-box',
                                pointerEvents: 'none',
                            }}
                        />
                    );
                }
                if (SHAPE_CLIP_PATHS[el.shapeType]) {
                    const drop = getDropShadowCss(el.shadow);
                    return (
                        <div style={{ width: '100%', height: '100%', filter: drop || 'none', pointerEvents: 'none' }}>
                            <div
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    backgroundColor: el.fillColour || '#ffffff',
                                    clipPath: SHAPE_CLIP_PATHS[el.shapeType],
                                }}
                            />
                        </div>
                    );
                }
                // rect
                return (
                    <div
                        style={{
                            width: '100%',
                            height: '100%',
                            backgroundColor: el.fillColour || '#ffffff',
                            border: (el.borderWidth ?? 1) > 0 ? `${el.borderWidth ?? 1}px solid ${el.borderColour || '#000000'}` : 'none',
                            borderRadius: `${el.borderRadius || 0}px`,
                            boxSizing: 'border-box',
                            pointerEvents: 'none',
                        }}
                    />
                );
            }

            default:
                return null;
        }
    };

    render() {
        const { template, zoom, elements, selectedElementId, selectedElementIds = [], onSelectElement, onMoveElement, onResizeElement, onRotateElement, onRadiusElement, bgColour, bgImage, bgImageOpacity, pageMarginX, pageMarginY, aiHighlightedIds } = this.props;
        const { showGrid } = this.state;
        const { pageSize, orientation } = template;

        const dimsPx = getPageDimsPx(pageSize, orientation);
        const dimsMm = getPageDimsMm(pageSize, orientation);

        const canvasW = dimsPx.w;
        const canvasH = dimsPx.h;

        const containerW = Math.round(canvasW * zoom);
        const containerH = Math.round(canvasH * zoom);

        return (
            <div
                style={{
                    flexGrow: 1,
                    overflowY: 'auto',
                    overflowX: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    padding: '16px',
                    backgroundColor: '#e9ecef',
                    minHeight: 0,
                }}
            >
                {/* Canvas outer container — clipped to zoomed size */}
                <div
                    style={{
                        width: containerW,
                        height: containerH,
                        overflow: 'hidden',
                        boxShadow: '0 2px 12px rgba(0,0,0,0.25)',
                        flexShrink: 0,
                    }}
                >
                    {/* Canvas inner — actual px size, scaled by transform */}
                    <div
                        ref={this.canvasRef}
                        style={{
                            position: 'relative',
                            width: canvasW,
                            height: canvasH,
                            backgroundColor: bgColour || '#ffffff',
                            transform: `scale(${zoom})`,
                            transformOrigin: '0 0',
                            ...(showGrid && (() => {
                                const gridPx = mmToPx(GRID_MM, 1);
                                return {
                                    backgroundImage: `linear-gradient(to right, #d0d0d0 1px, transparent 1px), linear-gradient(to bottom, #d0d0d0 1px, transparent 1px)`,
                                    backgroundSize: `${gridPx}px ${gridPx}px`,
                                };
                            })()),
                        }}
                        onDragOver={this._onDragOver}
                        onDrop={this._onDrop}
                        onClick={this._onCanvasClick}
                    >
                        {/* bg image as own layer so it can carry opacity */}
                        {bgImage && isSafeImageUrl(bgImage) && (
                            <div
                                style={{
                                    position: 'absolute',
                                    top: 0, left: 0, right: 0, bottom: 0,
                                    backgroundImage: `url(${bgImage})`,
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'center',
                                    opacity: (bgImageOpacity ?? 100) / 100,
                                    zIndex: 0,
                                    pointerEvents: 'none',
                                }}
                            />
                        )}

                        {/* Margin guide — dashed rectangle showing the printable area */}
                        {(pageMarginX > 0 || pageMarginY > 0) && (() => {
                            const mx = mmToPx(pageMarginX || 0, 1);
                            const my = mmToPx(pageMarginY || 0, 1);
                            return (
                                <div
                                    style={{
                                        position: 'absolute',
                                        top: my,
                                        left: mx,
                                        right: mx,
                                        bottom: my,
                                        border: '1px dashed rgba(0,123,255,0.4)',
                                        pointerEvents: 'none',
                                        zIndex: 9999,
                                    }}
                                />
                            );
                        })()}

                        {elements.map(el => (
                            <CanvasElement
                                key={el.id}
                                element={el}
                                zoom={zoom}
                                selected={selectedElementIds.includes(el.id) || selectedElementId === el.id}
                                multiSelected={selectedElementIds.length > 1}
                                isAiHighlighted={aiHighlightedIds ? aiHighlightedIds.has(el.id) : false}
                                canvasWMm={dimsMm.w}
                                canvasHMm={dimsMm.h}
                                onSelect={onSelectElement}
                                onMove={onMoveElement}
                                onResize={onResizeElement}
                                onRotate={onRotateElement}
                                onRadius={onRadiusElement}
                            >
                                {this._renderElementContent(el, zoom)}
                            </CanvasElement>
                        ))}
                    </div>
                </div>

                {/* Zoom + grid controls */}
                <div className="mt-2 d-flex align-items-center" style={{ gap: '6px' }}>
                    <span style={{ fontSize: '0.8rem', color: '#6c757d' }}>
                        <FormattedMessage id="TemplateDesigner.Zoom" />:
                    </span>
                    {[0.5, 0.75, 1.0].map(z => (
                        <button
                            key={z}
                            onClick={() => this.props.onZoomChange(z)}
                            style={{
                                padding: '2px 8px',
                                fontSize: '0.8rem',
                                border: '1px solid #ced4da',
                                borderRadius: 3,
                                backgroundColor: zoom === z ? '#007bff' : '#fff',
                                color: zoom === z ? '#fff' : '#495057',
                                cursor: 'pointer',
                            }}
                        >
                            {Math.round(z * 100)}%
                        </button>
                    ))}

                    <div style={{ width: 1, height: 18, backgroundColor: '#ced4da', margin: '0 4px' }} />

                    <button
                        onClick={() => this.setState(s => ({ showGrid: !s.showGrid }))}
                        title={`Grid ${GRID_MM}mm`}
                        style={{
                            padding: '2px 8px',
                            fontSize: '0.8rem',
                            border: '1px solid #ced4da',
                            borderRadius: 3,
                            backgroundColor: showGrid ? '#007bff' : '#fff',
                            color: showGrid ? '#fff' : '#495057',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4,
                        }}
                    >
                        <i className="fa-solid fa-border-all" style={{ fontSize: '0.75rem' }} />
                        <FormattedMessage id="TemplateDesigner.Grid" />
                    </button>
                </div>
            </div>
        );
    }
}

export default DesignerCanvas;
